// The shapes this module's API returns and accepts.

import { z } from "zod";
import {
  CALENDAR_KINDS,
  ENTITY_SOURCES,
  TASK_STATUSES,
  TASK_PRIORITIES,
} from "@/planning/model/vocabulary.js";

export { CALENDAR_KINDS, TASK_PRIORITIES, TASK_STATUSES };

export type CalendarKind = (typeof CALENDAR_KINDS)[number];

/** The source the calendar's reminders are sent as. */
export const REMINDER_NOTIFICATIONS = "calendar";

export type EntitySource = (typeof ENTITY_SOURCES)[number];

export type TaskStatus = (typeof TASK_STATUSES)[number];

export type TaskPriority = (typeof TASK_PRIORITIES)[number];

// Default calendars seeded on first run (mirrors the prototype legend).
export const DEFAULT_CALENDARS: { name: string; color: string; kind: CalendarKind }[] = [
  { name: "Personal", color: "#9b87f5", kind: "personal" },
  { name: "Finance", color: "#e3b04a", kind: "finance" },
  { name: "Holidays", color: "#e0556a", kind: "holidays" },
];

export interface Calendar {
  id: number;
  name: string;
  color: string;
  kind: CalendarKind;
  source: EntitySource;
}

export interface CalendarEvent {
  id: number;
  calendarId: number;
  title: string;
  description: string | null;
  location: string | null;
  start: string;
  end: string | null;
  allDay: boolean;
  recurrenceRule: string | null;
  // IANA origin zone for a timed event's wall-clock (drives DST-correct display
  // and recurrence). null for an all-day event, or one stored without a zone.
  // See docs/timezones.md.
  timezone: string | null;
  // Per-occurrence exceptions to a recurring series (mirrors iCalendar's EXDATE
  // + RECURRENCE-ID) — set only on an override row: the series master this
  // replaces, and the original UTC instant it replaces. See docs/timezones.md.
  masterEventId: number | null;
  recurrenceId: string | null;
  // Occurrences deleted outright with no override — meaningful only on a
  // recurring master.
  exdates: string[];
  tags: string[];
  reminders: number[]; // minutes-before, ascending
  source: EntitySource;
  externalId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Task {
  id: number;
  title: string;
  notes: string | null;
  dueAt: string | null;
  priority: TaskPriority;
  status: TaskStatus;
  completedAt: string | null;
  calendarId: number | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

// A country available from the live public-holiday source, for the Holidays
// calendar's location picker.
export interface HolidayCountry {
  code: string;
  name: string;
}

// ---- calendars ----
export const createCalendarSchema = z.object({
  name: z.string().min(1).max(120),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Expected a hex color like #9b87f5"),
  kind: z.enum(CALENDAR_KINDS).default("other"),
});

export const updateCalendarSchema = createCalendarSchema.partial();

export type CreateCalendarInput = z.infer<typeof createCalendarSchema>;

export type UpdateCalendarInput = z.infer<typeof updateCalendarSchema>;

// ---- events ----
// Datetimes are ISO 8601 strings (UTC) on the wire.
const isoDate = z.string().datetime({ offset: true });

// Reminder offsets in minutes-before-start (0 = at start, max 4 weeks).
const reminderOffsets = z.array(z.number().int().min(0).max(40320)).max(10);

// An IANA zone `Intl` actually accepts — the origin zone a timed event's
// wall-clock is anchored to. A bogus value would throw inside Intl on the
// recurrence/expansion path, so validate here rather than trust the string.
const ianaZone = z
  .string()
  .min(1)
  .max(64)
  .refine((tz) => {
    try {
      new Intl.DateTimeFormat("en-CA", { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  }, "Unknown IANA timezone");

export const createEventSchema = z
  .object({
    calendarId: z.number().int().positive(),
    title: z.string().min(1).max(300),
    description: z.string().max(5000).optional(),
    location: z.string().max(300).optional(),
    start: isoDate,
    end: isoDate.optional(),
    allDay: z.boolean().default(false),
    recurrenceRule: z.string().max(500).optional(),
    timezone: ianaZone.optional(),
    tags: z.array(z.string().max(60)).default([]),
    reminders: reminderOffsets.default([]),
  })
  .refine((v) => !v.end || v.end >= v.start, {
    message: "end must be after start",
    path: ["end"],
  });

export const updateEventSchema = z
  .object({
    calendarId: z.number().int().positive().optional(),
    title: z.string().min(1).max(300).optional(),
    description: z.string().max(5000).nullable().optional(),
    location: z.string().max(300).nullable().optional(),
    start: isoDate.optional(),
    end: isoDate.nullable().optional(),
    allDay: z.boolean().optional(),
    recurrenceRule: z.string().max(500).nullable().optional(),
    timezone: ianaZone.nullable().optional(),
    tags: z.array(z.string().max(60)).optional(),
    reminders: reminderOffsets.optional(),
  })
  .strict();

export type CreateEventInput = z.infer<typeof createEventSchema>;

export type UpdateEventInput = z.infer<typeof updateEventSchema>;

export const eventRangeQuerySchema = z.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
  calendarId: z.coerce.number().int().positive().optional(),
});

// Scopes a PATCH/DELETE on /events/:id to one occurrence of a recurring series
// rather than the whole series (the default). `:id` is always the series
// master's id; `occurrenceStart` identifies which occurrence by its original,
// unmodified UTC instant. See docs/timezones.md.
export const eventOccurrenceScopeSchema = z
  .object({
    scope: z.enum(["this", "all"]).default("all"),
    occurrenceStart: isoDate.optional(),
  })
  .refine((v) => v.scope !== "this" || v.occurrenceStart != null, {
    message: 'occurrenceStart is required when scope is "this"',
    path: ["occurrenceStart"],
  });

// ---- tasks ----
export const createTaskSchema = z.object({
  title: z.string().min(1).max(300),
  notes: z.string().max(5000).optional(),
  dueAt: isoDate.optional(),
  priority: z.enum(TASK_PRIORITIES).default("normal"),
  calendarId: z.number().int().positive().nullable().optional(),
  tags: z.array(z.string().max(60)).default([]),
});

export const updateTaskSchema = z
  .object({
    title: z.string().min(1).max(300).optional(),
    notes: z.string().max(5000).nullable().optional(),
    dueAt: isoDate.nullable().optional(),
    priority: z.enum(TASK_PRIORITIES).optional(),
    status: z.enum(TASK_STATUSES).optional(),
    calendarId: z.number().int().positive().nullable().optional(),
    tags: z.array(z.string().max(60)).optional(),
  })
  .strict();

export type CreateTaskInput = z.infer<typeof createTaskSchema>;

export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;

export const taskListQuerySchema = z.object({
  status: z.enum(TASK_STATUSES).optional(),
});

// ---- app settings ----
// A user-picked location that drives the Holidays calendar — countries come
// from a live public-holiday API (not a hardcoded list), and `subdivision` is
// an optional ISO 3166-2 code (e.g. "CH-GE") to include that region's local
// holidays alongside the country's nationwide ones.
export const holidayLocationSchema = z.object({
  countryCode: z.string().length(2),
  countryName: z.string().min(1).max(100),
  subdivision: z.string().max(20).nullable().optional(),
});

export const updateHolidayLocationsSchema = z.object({
  locations: z.array(holidayLocationSchema).max(20),
});

export type HolidayLocation = z.infer<typeof holidayLocationSchema>;

export const updateDefaultImportCalendarIdSchema = z.object({
  calendarId: z.number().int().positive().nullable(),
});
